import { z } from 'zod'

export const HUB_SPEC_VERSION = '1'

export const HUB_SECTION_TYPES = [
  'hero',
  'intro',
  'anchorLinkList',
  'topPicks',
  'productRail',
  'reportGrid',
  'guideSteps',
  'guidePathways',
  'decisionBlock',
  'concernLinks',
  'comparisonTable',
  'faq',
  'sourcesSummary',
  'ctaBand',
  'pullQuote',
  'statCallout',
  'methodologyCard',
] as const

export const HUB_LAYOUT_MODES = ['structured', 'canvas'] as const

export const HUB_LAYOUT_HINTS = [
  'full',
  'twoColumn',
  'featureAside',
  'compactGrid',
  'denseList',
] as const

export const HUB_KINDS = ['informational', 'transactional'] as const

const NonEmptyStringSchema = z.string().trim().min(1)
const OptionalTextSchema = z.string().trim().min(1).optional()
const LooseCountSchema = z.number()
const NonNegativeNumberSchema = z
  .number()
  .refine((value) => value >= 0, 'Expected a non-negative number')
const IntegerNumberSchema = z
  .number()
  .refine(Number.isInteger, 'Expected an integer')
const NonNegativeIntegerSchema = IntegerNumberSchema.refine(
  (value) => value >= 0,
  'Expected a non-negative integer'
)
const PositiveIntegerSchema = IntegerNumberSchema.refine(
  (value) => value > 0,
  'Expected a positive integer'
)
const ScoreNumberSchema = z
  .number()
  .refine((value) => value >= 0 && value <= 100, 'Expected a score from 0-100')

export const HubKindSchema = z.enum(HUB_KINDS)
export const HubLayoutModeSchema = z.enum(HUB_LAYOUT_MODES)
export const HubLayoutHintSchema = z.enum(HUB_LAYOUT_HINTS)
export const HubSectionTypeSchema = z.enum(HUB_SECTION_TYPES)
export const HubReportAssignmentRoleSchema = z.enum(['primary', 'supporting'])

// URL validators use `.refine()` instead of `.regex()` / `.url()` so the
// emitted JSON Schema is a plain `{ "type": "string" }`. Gemini's
// `responseSchema` runs regexes through RE2 (no lookahead, no lookbehind)
// and rejects unsupported patterns at request time. The validation still
// runs on the Zod side when we narrow the agent output.
export const HubInternalHrefSchema = z
  .string()
  .trim()
  .refine(
    (value) => value.startsWith('/') && !value.startsWith('//'),
    'Expected an internal absolute path (starts with "/" and not "//")'
  )

// Decision blocks support two CTA shapes: a sibling-page handoff ("/path",
// optionally with an anchor) or an in-page jump ("#section-id") — the tone
// field's "neutral" variant exists for the latter.
export const HubInternalOrInPageHrefSchema = z
  .string()
  .trim()
  .refine(
    (value) =>
      (value.startsWith('/') && !value.startsWith('//')) ||
      (value.startsWith('#') && value.length > 1),
    'Expected an internal absolute path (starts with "/" and not "//") or an in-page anchor ("#section-id")'
  )

export const HubExternalUrlSchema = z
  .string()
  .trim()
  .refine((value) => {
    try {
      const url = new URL(value)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  }, 'Expected an http(s) URL')

export const HubBuyLinkSchema = z
  .object({
    label: NonEmptyStringSchema,
    url: HubExternalUrlSchema,
    price: NonNegativeNumberSchema.nullable().optional(),
    currency: z
      .string()
      .trim()
      .regex(/^[A-Z]{3}$/)
      .nullable()
      .optional(),
    logoUrl: HubExternalUrlSchema.nullable().optional(),
    verified: z.boolean().optional(),
  })
  .strict()

export const HubProductRefSchema = z
  .object({
    productId: NonEmptyStringSchema,
    slug: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    brand: z.string().trim().min(1).nullable().optional(),
    image: HubExternalUrlSchema.nullable().optional(),
    summary: z.string().trim().min(1).nullable().optional(),
    priceLabel: z.string().trim().min(1).nullable().optional(),
    buyLinks: z.array(HubBuyLinkSchema).default([]),
  })
  .strict()

export const HubReportSourceKindSchema = z.enum([
  'official',
  'retail',
  'review',
  'social',
  'community',
  'other',
])

export const HubReportSourceLinkSchema = z
  .object({
    url: HubExternalUrlSchema,
    label: NonEmptyStringSchema,
    domain: NonEmptyStringSchema,
    productNames: z.array(NonEmptyStringSchema).default([]),
    kind: HubReportSourceKindSchema,
  })
  .strict()

export const HubSourceSummarySchema = z
  .object({
    sourceLinks: z.array(HubReportSourceLinkSchema).default([]),
    productCount: NonNegativeIntegerSchema.default(0),
    sourcesReviewed: NonNegativeIntegerSchema.nullable().optional(),
  })
  .strict()

export const HubReportRefSchema = z
  .object({
    reportId: NonEmptyStringSchema,
    slug: NonEmptyStringSchema,
    title: NonEmptyStringSchema,
    imageUrl: HubExternalUrlSchema.nullable().optional(),
    href: HubInternalHrefSchema,
    excerpt: z.string().trim().min(1).nullable().optional(),
    categorySlug: z.string().trim().min(1).nullable().optional(),
    subcategorySlug: z.string().trim().min(1).nullable().optional(),
    updatedAt: z.string().datetime().nullable().optional(),
  })
  .strict()

export const HubReportCandidateAnchorSchema = z
  .object({
    label: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
    sectionId: NonEmptyStringSchema,
    sectionType: NonEmptyStringSchema,
  })
  .strict()

export const HubReportCandidateQuickAnswerSchema = z
  .object({
    title: NonEmptyStringSchema,
    body: NonEmptyStringSchema,
    productId: NonEmptyStringSchema.nullable().optional(),
  })
  .strict()

export const HubReportCandidateCriterionSchema = z
  .object({
    label: NonEmptyStringSchema,
    description: NonEmptyStringSchema,
  })
  .strict()

export const HubReportCandidateHowWePickedSchema = z
  .object({
    intro: z.string().trim().min(1).nullable().optional(),
    criteria: z.array(HubReportCandidateCriterionSchema).default([]),
  })
  .strict()

export const HubReportCandidateFaqSchema = z
  .object({
    question: NonEmptyStringSchema,
    answer: NonEmptyStringSchema,
  })
  .strict()

export const HubReportCandidateComparisonFactSchema = z
  .object({
    label: NonEmptyStringSchema,
    value: NonEmptyStringSchema,
  })
  .strict()

export const HubReportCandidateTopProductSchema = z
  .object({
    productId: NonEmptyStringSchema,
    productSlug: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    brand: z.string().trim().min(1).nullable().optional(),
    roleLabel: z.string().trim().min(1).nullable().optional(),
    summary: z.string().trim().min(1).nullable().optional(),
    sourceHref: HubInternalHrefSchema,
    bestFor: z.array(NonEmptyStringSchema).default([]),
    notFor: z.array(NonEmptyStringSchema).default([]),
    pros: z.array(NonEmptyStringSchema).default([]),
    cons: z.array(NonEmptyStringSchema).default([]),
    tradeoffs: z.array(NonEmptyStringSchema).default([]),
    comparisonFacts: z
      .array(HubReportCandidateComparisonFactSchema)
      .default([]),
  })
  .strict()

export const HubReportCandidatePainPointSchema = z
  .object({
    concern: NonEmptyStringSchema,
    recommendation: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
    productId: NonEmptyStringSchema.nullable().optional(),
  })
  .strict()

export const HubReportCandidateEvidenceSchema = z
  .object({
    productsAnalyzed: NonNegativeIntegerSchema,
    sourcesReviewed: NonNegativeIntegerSchema,
    youtubeCount: NonNegativeIntegerSchema.default(0),
    redditCount: NonNegativeIntegerSchema.default(0),
    tiktokCount: NonNegativeIntegerSchema.default(0),
    instagramCount: NonNegativeIntegerSchema.default(0),
  })
  .strict()

export const HubReportCandidateSchema = z
  .object({
    reportId: NonEmptyStringSchema,
    slug: NonEmptyStringSchema,
    title: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
    role: HubReportAssignmentRoleSchema.default('supporting'),
    sortOrder: NonNegativeIntegerSchema.default(0),
    categorySlug: z.string().trim().min(1).nullable().optional(),
    subcategorySlug: z.string().trim().min(1).nullable().optional(),
    summary: z.string().trim().min(1).nullable().optional(),
    imageUrl: HubExternalUrlSchema.nullable().optional(),
    updatedAt: z.string().datetime().nullable().optional(),
    quickAnswer: HubReportCandidateQuickAnswerSchema.optional(),
    whoItsFor: z.string().trim().min(1).nullable().optional(),
    howWePicked: HubReportCandidateHowWePickedSchema.optional(),
    whatToKnow: z.array(HubReportCandidateFaqSchema).default([]),
    topProducts: z.array(HubReportCandidateTopProductSchema).default([]),
    painPoints: z.array(HubReportCandidatePainPointSchema).default([]),
    anchorLinks: z.array(HubReportCandidateAnchorSchema).default([]),
    evidence: HubReportCandidateEvidenceSchema.optional(),
  })
  .strict()

export const HubRenderContextSchema = z
  .object({
    productsById: z.record(NonEmptyStringSchema, HubProductRefSchema),
    reportsById: z.record(NonEmptyStringSchema, HubReportRefSchema),
    sourceSummary: HubSourceSummarySchema.optional(),
  })
  .strict()

export const HubBylineSchema = z
  .object({
    name: NonEmptyStringSchema,
    role: z.string().trim().min(1).nullable().optional(),
    avatarUrl: HubExternalUrlSchema.nullable().optional(),
  })
  .strict()

export const HubBreadcrumbSchema = z
  .object({
    label: NonEmptyStringSchema,
    href: HubInternalHrefSchema.nullable(),
  })
  .strict()

const HubSectionBaseSchema = z.object({
  id: NonEmptyStringSchema,
  type: HubSectionTypeSchema,
  eyebrow: OptionalTextSchema,
  title: OptionalTextSchema,
  dek: OptionalTextSchema,
  body: OptionalTextSchema,
  sourceReportIds: z.array(NonEmptyStringSchema).optional(),
  sourceProductIds: z.array(NonEmptyStringSchema).optional(),
  layoutHint: HubLayoutHintSchema.optional(),
})

export const HubHeroSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('hero'),
  title: NonEmptyStringSchema,
  kicker: OptionalTextSchema,
  breadcrumbs: z.array(HubBreadcrumbSchema).optional(),
  byline: HubBylineSchema.optional(),
  metaLine: OptionalTextSchema,
  heroImage: z
    .object({
      url: HubExternalUrlSchema,
      alt: NonEmptyStringSchema,
      aspect: z.enum(['editorial', 'wide', 'square']).optional(),
    })
    .strict()
    .optional(),
}).strict()

export const HubIntroSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('intro'),
  body: NonEmptyStringSchema,
}).strict()

export const HubAnchorLinkListItemSchema = z
  .object({
    label: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
    description: OptionalTextSchema,
    groupLabel: OptionalTextSchema,
  })
  .strict()

const HubLooseLinkItemSchema = z
  .object({
    label: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
    description: OptionalTextSchema,
    groupLabel: OptionalTextSchema,
  })
  .strict()

export const HubAnchorLinkListSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('anchorLinkList'),
  links: z.array(HubAnchorLinkListItemSchema).min(1),
}).strict()

export const HubTopPicksPickSchema = z
  .object({
    productId: NonEmptyStringSchema.describe(
      'Product ID from the assigned products list'
    ),
    label: NonEmptyStringSchema.describe(
      'Role label for this pick (e.g. "Best Overall", "Best Budget")'
    ),
    reason: NonEmptyStringSchema.describe(
      'Comparative reasoning, 35-60 words. Inline anchor links encouraged.'
    ),
    sourceReportId: NonEmptyStringSchema.optional().describe(
      'Report ID this pick was sourced from'
    ),
  })
  .strict()

export const HubTopPicksSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('topPicks'),
  picks: z.array(HubTopPicksPickSchema).min(1),
  moreLink: z
    .object({
      label: NonEmptyStringSchema,
      href: HubInternalHrefSchema,
    })
    .strict()
    .optional(),
}).strict()

export const HubProductRailSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('productRail'),
  productIds: z.array(NonEmptyStringSchema).min(1),
}).strict()

export const HubReportGridItemSchema = z
  .object({
    reportId: NonEmptyStringSchema,
    title: OptionalTextSchema,
    blurb: OptionalTextSchema,
  })
  .strict()

export const HubReportGridSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('reportGrid'),
  items: z.array(HubReportGridItemSchema).min(1),
}).strict()

export const HubGuideStepSchema = z
  .object({
    index: PositiveIntegerSchema.optional(),
    label: NonEmptyStringSchema,
    description: OptionalTextSchema,
    optional: z.boolean().optional(),
  })
  .strict()

export const HubGuideStepsSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('guideSteps'),
  steps: z.array(HubGuideStepSchema).min(1),
  truncatedTailLabel: OptionalTextSchema,
}).strict()

export const HubGuidePathwayItemSchema = z
  .object({
    heading: NonEmptyStringSchema,
    subheading: OptionalTextSchema,
    body: NonEmptyStringSchema,
    reportId: NonEmptyStringSchema,
    linkLabel: OptionalTextSchema,
  })
  .strict()

export const HubGuidePathwaysSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('guidePathways'),
  pathways: z.array(HubGuidePathwayItemSchema).min(1),
}).strict()

export const HubDecisionBlockSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('decisionBlock'),
  tone: z.enum(['info', 'neutral']),
  kicker: NonEmptyStringSchema.describe('Short headline for the callout'),
  linkLabel: NonEmptyStringSchema.describe('CTA button text'),
  linkHref: HubInternalOrInPageHrefSchema.describe(
    'Internal absolute path or in-page anchor ("#section-id") the CTA links to'
  ),
  supportText: OptionalTextSchema.describe(
    'Supporting copy below the kicker. Inline anchor links allowed.'
  ),
}).strict()

export const HubConcernLinkItemSchema = z
  .object({
    label: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
  })
  .strict()

export const HubConcernLinksSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('concernLinks'),
  prompt: OptionalTextSchema,
  links: z.array(HubConcernLinkItemSchema).min(1),
}).strict()

export const HubComparisonColumnSchema = z
  .object({
    slug: NonEmptyStringSchema.describe(
      'Unique column key (use the productId or a short identifier)'
    ),
    name: NonEmptyStringSchema.describe('Display label for the column header'),
    isHighlighted: z.boolean().optional(),
  })
  .strict()

export const HubComparisonRowSchema = z
  .object({
    label: NonEmptyStringSchema.describe(
      'Dimension name for this row (e.g. "Fabric", "Price range")'
    ),
    values: z
      .array(z.string().trim().min(1).nullable())
      .describe('One value per column, same order as columns array'),
  })
  .strict()

export const HubComparisonTableSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('comparisonTable'),
  columns: z.array(HubComparisonColumnSchema).min(1),
  rows: z.array(HubComparisonRowSchema).min(1),
}).strict()

export const HubFaqItemSchema = z
  .object({
    question: NonEmptyStringSchema,
    answer: NonEmptyStringSchema,
  })
  .strict()

export const HubFaqSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('faq'),
  items: z.array(HubFaqItemSchema).min(1),
}).strict()

export const HubSourcesSummarySectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('sourcesSummary'),
  totalSources: NonNegativeIntegerSchema,
  breakdown: z
    .array(
      z
        .object({
          kind: NonEmptyStringSchema,
          count: NonNegativeIntegerSchema,
        })
        .strict()
    )
    .optional(),
  href: HubInternalHrefSchema.optional(),
}).strict()

export const HubCtaLinkSchema = z
  .object({
    label: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
  })
  .strict()

export const HubCtaBandSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('ctaBand'),
  primary: HubCtaLinkSchema,
  secondary: HubCtaLinkSchema.optional(),
}).strict()

export const HubPullQuoteSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('pullQuote'),
  quote: NonEmptyStringSchema,
  attribution: OptionalTextSchema,
  attributionRole: OptionalTextSchema,
  sourceReportId: NonEmptyStringSchema.optional(),
}).strict()

export const HubStatCalloutItemSchema = z
  .object({
    value: NonEmptyStringSchema,
    label: NonEmptyStringSchema,
    helpText: OptionalTextSchema,
  })
  .strict()

export const HubStatCalloutSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('statCallout'),
  stats: z.array(HubStatCalloutItemSchema).min(1),
}).strict()

export const HubMethodologyCardSectionSchema = HubSectionBaseSchema.extend({
  type: z.literal('methodologyCard'),
  kicker: OptionalTextSchema,
  body: NonEmptyStringSchema,
  linkLabel: NonEmptyStringSchema,
  linkHref: HubInternalHrefSchema,
  sourceReportId: NonEmptyStringSchema.optional(),
}).strict()

export const HubSectionSchema = z.discriminatedUnion('type', [
  HubHeroSectionSchema,
  HubIntroSectionSchema,
  HubAnchorLinkListSectionSchema,
  HubTopPicksSectionSchema,
  HubProductRailSectionSchema,
  HubReportGridSectionSchema,
  HubGuideStepsSectionSchema,
  HubGuidePathwaysSectionSchema,
  HubDecisionBlockSectionSchema,
  HubConcernLinksSectionSchema,
  HubComparisonTableSectionSchema,
  HubFaqSectionSchema,
  HubSourcesSummarySectionSchema,
  HubCtaBandSectionSchema,
  HubPullQuoteSectionSchema,
  HubStatCalloutSectionSchema,
  HubMethodologyCardSectionSchema,
])

export const HUB_SECTION_SCHEMAS = {
  hero: HubHeroSectionSchema,
  intro: HubIntroSectionSchema,
  anchorLinkList: HubAnchorLinkListSectionSchema,
  topPicks: HubTopPicksSectionSchema,
  productRail: HubProductRailSectionSchema,
  reportGrid: HubReportGridSectionSchema,
  guideSteps: HubGuideStepsSectionSchema,
  guidePathways: HubGuidePathwaysSectionSchema,
  decisionBlock: HubDecisionBlockSectionSchema,
  concernLinks: HubConcernLinksSectionSchema,
  comparisonTable: HubComparisonTableSectionSchema,
  faq: HubFaqSectionSchema,
  sourcesSummary: HubSourcesSummarySectionSchema,
  ctaBand: HubCtaBandSectionSchema,
  pullQuote: HubPullQuoteSectionSchema,
  statCallout: HubStatCalloutSectionSchema,
  methodologyCard: HubMethodologyCardSectionSchema,
} satisfies Record<HubSectionType, z.ZodTypeAny>

export function getHubSectionSchema(type: HubSectionType): z.ZodTypeAny {
  return HUB_SECTION_SCHEMAS[type]
}

export const HubReportEvidenceAnchorSchema = z
  .object({
    label: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
    sectionType: NonEmptyStringSchema,
  })
  .strict()

export const HubReportEvidencePickSchema = z
  .object({
    productId: NonEmptyStringSchema,
    roleLabel: NonEmptyStringSchema,
    reason: NonEmptyStringSchema,
    sourceHref: HubInternalHrefSchema,
  })
  .strict()

export const HubReportEvidenceDimensionSchema = z
  .object({
    label: NonEmptyStringSchema,
    summary: NonEmptyStringSchema,
  })
  .strict()

export const HubReportEvidencePainPointSchema = z
  .object({
    concern: NonEmptyStringSchema,
    recommendation: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
  })
  .strict()

export const HubReportEvidenceCountsSchema = z
  .object({
    productsAnalyzed: NonNegativeIntegerSchema,
    sourcesReviewed: NonNegativeIntegerSchema,
    youtubeCount: NonNegativeIntegerSchema.default(0),
    redditCount: NonNegativeIntegerSchema.default(0),
  })
  .strict()

export const HubReportEvidenceSchema = z
  .object({
    reportId: NonEmptyStringSchema,
    title: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
    oneLineTakeaway: NonEmptyStringSchema,
    strongestClaim: NonEmptyStringSchema,
    primaryAudience: NonEmptyStringSchema.nullable(),
    strongestPick: HubReportEvidencePickSchema.nullable(),
    comparisonDimensions: z.array(HubReportEvidenceDimensionSchema).default([]),
    painPoints: z.array(HubReportEvidencePainPointSchema).default([]),
    anchorLinks: z.array(HubReportEvidenceAnchorSchema).default([]),
    evidenceCounts: HubReportEvidenceCountsSchema,
  })
  .strict()

export const HubPageBlueprintSectionPlanSchema = z
  .object({
    id: NonEmptyStringSchema,
    type: HubSectionTypeSchema,
    intent: NonEmptyStringSchema,
    evidenceReportIds: z.array(NonEmptyStringSchema).default([]),
    evidenceProductIds: z.array(NonEmptyStringSchema).default([]),
    anchorHrefs: z.array(HubInternalHrefSchema).default([]),
    layoutIntent: NonEmptyStringSchema.optional(),
  })
  .strict()

export const HubPageBlueprintSchema = z
  .object({
    pageThesis: NonEmptyStringSchema,
    readerPromise: NonEmptyStringSchema,
    /** Agent-authored text-to-image prompt for the page's unique hero image. */
    /**
     * Agent-authored text-to-image prompt for the page's hero image.
     * Optional on purpose: the hero image is decorative and the renderer
     * degrades to a text-only hero, so a model that omits this must never
     * fail blueprint validation (and with it the whole page).
     */
    heroImagePrompt: NonEmptyStringSchema.optional(),
    sectionPlan: z.array(HubPageBlueprintSectionPlanSchema).min(1),
    featuredReportIds: z.array(NonEmptyStringSchema).default([]),
    featuredProductIds: z.array(NonEmptyStringSchema).default([]),
    longTailReportTreatment: z.enum(['grid', 'anchorList', 'sourceSummary']),
  })
  .strict()

export const HubCanvasResearchQuerySchema = z
  .object({
    query: NonEmptyStringSchema,
    purpose: NonEmptyStringSchema,
  })
  .strict()

export const HubCanvasResearchPlanSchema = z
  .object({
    rationale: NonEmptyStringSchema,
    webSearchQueries: z.array(HubCanvasResearchQuerySchema).default([]),
    inspectReportIds: z.array(NonEmptyStringSchema).default([]),
    inspectProductIds: z.array(NonEmptyStringSchema).default([]),
  })
  .strict()

export const HubCanvasSearchSourceSchema = z
  .object({
    title: NonEmptyStringSchema.nullable(),
    url: HubExternalUrlSchema.nullable(),
  })
  .strict()

export const HubCanvasSearchFindingSchema = z
  .object({
    query: NonEmptyStringSchema,
    purpose: NonEmptyStringSchema,
    answer: NonEmptyStringSchema.nullable(),
    sources: z.array(HubCanvasSearchSourceSchema).default([]),
  })
  .strict()

export const HubCanvasResearchBriefSchema = z
  .object({
    articleAngle: NonEmptyStringSchema,
    wirecutterThesis: NonEmptyStringSchema,
    marketContext: NonEmptyStringSchema,
    evaluationCriteria: z.array(NonEmptyStringSchema).default([]),
    editorialFindings: z.array(NonEmptyStringSchema).default([]),
    caveats: z.array(NonEmptyStringSchema).default([]),
    searchFindings: z.array(HubCanvasSearchFindingSchema).default([]),
    inspectedReportIds: z.array(NonEmptyStringSchema).default([]),
    inspectedProductIds: z.array(NonEmptyStringSchema).default([]),
    toolSummary: NonEmptyStringSchema,
  })
  .strict()

export const HubEditorialReviewSchema = z
  .object({
    score: ScoreNumberSchema,
    blockingIssues: z.array(NonEmptyStringSchema).default([]),
    warnings: z.array(NonEmptyStringSchema).default([]),
    sectionNotes: z.array(NonEmptyStringSchema).default([]),
  })
  .strict()

/**
 * Flat section shape used ONLY as the LLM's structured-output schema. Provider
 * structured-output modes (Gemini / Vertex `responseSchema`) reject `oneOf`,
 * which is what Zod emits for a discriminated union. We accept a permissive
 * superset of every section type's fields, then narrow each section through
 * the strict {@link HubSectionSchema} in `parseHubPageSpec`. Sections that
 * fail to narrow are dropped before validation.
 *
 * Every field except `id`, `type`, and the section title is optional here —
 * runtime narrowing enforces per-type required fields.
 */
export const HubSectionLooseSchema = z.object({
  id: NonEmptyStringSchema.describe(
    'Unique section identifier within the page'
  ),
  type: HubSectionTypeSchema.describe(
    'Section component type. Must be one of the allowed types.'
  ),
  eyebrow: OptionalTextSchema.describe('Small label above the section title'),
  title: OptionalTextSchema.describe('Section heading text'),
  dek: OptionalTextSchema.describe(
    'Subtitle or short description below the title'
  ),
  body: OptionalTextSchema.describe(
    'Body text. Use \\n\\n for paragraph breaks. Inline anchor links allowed: [label](/reports/slug#anchor)'
  ),
  sourceReportIds: z
    .array(NonEmptyStringSchema)
    .optional()
    .describe('Report IDs this section draws evidence from'),
  sourceProductIds: z
    .array(NonEmptyStringSchema)
    .optional()
    .describe('Product IDs referenced in this section'),
  layoutHint: HubLayoutHintSchema.optional(),

  // Hero
  kicker: OptionalTextSchema.describe(
    'Hero: short category label above the H1 (e.g. "Buyer\'s guide")'
  ),
  breadcrumbs: z
    .array(HubBreadcrumbSchema)
    .optional()
    .describe('Hero: navigation breadcrumbs'),
  byline: HubBylineSchema.optional(),
  metaLine: OptionalTextSchema.describe(
    'Hero: trust-signal line (e.g. "3 reports · 73 products analyzed")'
  ),
  heroImage: z
    .object({
      url: HubExternalUrlSchema,
      alt: NonEmptyStringSchema,
      aspect: z.enum(['editorial', 'wide', 'square']).optional(),
    })
    .optional()
    .describe('Hero: optional featured image'),

  // AnchorLinkList / ConcernLinks
  links: z
    .array(HubLooseLinkItemSchema)
    .optional()
    .describe(
      'Flat array of links. For anchorLinkList: one raw /reports/<slug> link per report, with no hash anchor. For concernLinks: chips routing by concern/persona.'
    ),

  // TopPicks
  picks: z
    .array(HubTopPicksPickSchema)
    .optional()
    .describe(
      'Curated product picks with productId, label (role), reason (35-60 words with comparative reasoning), and sourceReportId'
    ),
  moreLink: z
    .object({
      label: NonEmptyStringSchema,
      href: HubInternalHrefSchema,
    })
    .optional()
    .describe('TopPicks: optional "see more" link'),

  // ProductRail
  productIds: z
    .array(NonEmptyStringSchema)
    .optional()
    .describe('ProductRail: product IDs for horizontal browsing strip'),

  // ReportGrid / Faq items
  items: z
    .array(z.unknown())
    .optional()
    .describe(
      'For reportGrid: array of { reportId, title?, blurb? }. For faq: array of { question, answer }.'
    ),

  // GuideSteps
  steps: z
    .array(
      z
        .object({
          index: LooseCountSchema.optional(),
          label: NonEmptyStringSchema.describe('Step heading'),
          description: OptionalTextSchema.describe(
            'Step body, 2-3 sentences. Inline anchor links allowed.'
          ),
          optional: z.boolean().optional(),
        })
        .strict()
    )
    .optional()
    .describe('GuideSteps: ordered how-to or decision steps'),
  truncatedTailLabel: OptionalTextSchema,

  // GuidePathways
  pathways: z
    .array(
      z
        .object({
          heading: NonEmptyStringSchema.describe(
            'Reader problem, occasion, use case, or consumption context'
          ),
          subheading: OptionalTextSchema.describe(
            'Short framing line below the heading'
          ),
          body: NonEmptyStringSchema.describe(
            '2-4 sentences that synthesize why this situation matters. Inline anchor links allowed.'
          ),
          reportId: NonEmptyStringSchema.describe(
            'Assigned report ID that backs this pathway'
          ),
          linkLabel: OptionalTextSchema.describe('Optional report link label'),
        })
        .strict()
    )
    .optional()
    .describe(
      'GuidePathways: full-width report-backed pathways for reader problems, occasions, or use cases'
    ),

  // DecisionBlock
  tone: z
    .enum(['info', 'neutral'])
    .optional()
    .describe(
      'DecisionBlock: "info" for sibling-hub handoff, "neutral" for in-page jumps'
    ),
  linkLabel: OptionalTextSchema.describe('DecisionBlock: CTA button text'),
  linkHref: HubInternalOrInPageHrefSchema.optional().describe(
    'DecisionBlock: internal absolute path, or in-page anchor like "#section-id" pointing at another section of this page'
  ),
  supportText: OptionalTextSchema.describe(
    'DecisionBlock: supporting copy. Inline anchor links allowed.'
  ),

  // ConcernLinks
  prompt: OptionalTextSchema.describe(
    'ConcernLinks: optional prompt text above the chips'
  ),

  // ComparisonTable
  columns: z
    .array(HubComparisonColumnSchema)
    .optional()
    .describe(
      'ComparisonTable: column definitions with slug (product ID or unique key) and name (display label)'
    ),
  rows: z
    .array(HubComparisonRowSchema)
    .optional()
    .describe(
      'ComparisonTable: rows with label (dimension name) and values array (one per column, same order)'
    ),

  // SourcesSummary
  totalSources: LooseCountSchema.optional().describe(
    'SourcesSummary: total evidence count across all reports'
  ),
  breakdown: z
    .array(
      z
        .object({
          kind: NonEmptyStringSchema.describe(
            'Category label (e.g. "reports", "YouTube reviews", "Reddit threads")'
          ),
          count: LooseCountSchema.describe('Count for this category'),
        })
        .strict()
    )
    .optional()
    .describe('SourcesSummary: evidence breakdown by category'),
  href: HubInternalHrefSchema.optional(),

  // CtaBand
  primary: HubCtaLinkSchema.optional().describe(
    'CtaBand: primary CTA with label and internal href'
  ),
  secondary: HubCtaLinkSchema.optional().describe(
    'CtaBand: optional secondary CTA'
  ),

  // PullQuote
  quote: OptionalTextSchema.describe(
    'PullQuote: the quoted sentence from a report or expert source'
  ),
  attribution: OptionalTextSchema.describe(
    'PullQuote: who said it (e.g. "Dupe Research Team")'
  ),
  attributionRole: OptionalTextSchema.describe(
    'PullQuote: role or context (e.g. "Moisture-Wicking Leggings Report")'
  ),
  sourceReportId: NonEmptyStringSchema.optional().describe(
    'PullQuote/MethodologyCard: report ID the quote or methodology is sourced from'
  ),

  // StatCallout
  stats: z
    .array(HubStatCalloutItemSchema)
    .optional()
    .describe(
      'StatCallout: 2-4 trust-signal tiles with value (e.g. "793"), label (e.g. "sources reviewed"), and optional helpText'
    ),
})
// Default strip mode: silently drops unknown fields at parse time AND
// emits `additionalProperties: false` in the JSON Schema handed to the
// provider. Gemini rejects `additionalProperties: true` (what
// `.passthrough()` would emit), so do not switch this back to passthrough.

export const HubSeoSchema = z
  .object({
    title: NonEmptyStringSchema.describe(
      'SEO title tag (include " | Dupe" suffix)'
    ),
    description: NonEmptyStringSchema.describe(
      'Meta description for search results, 120-160 chars'
    ),
    canonicalPath: HubInternalHrefSchema.describe(
      'Canonical path starting with / (e.g. "/reports/slug")'
    ),
  })
  .strict()

export const HubInternalLinkSchema = z
  .object({
    label: NonEmptyStringSchema,
    href: HubInternalHrefSchema,
    relationship: z
      .enum(['siblingHub', 'childReport', 'parentHub', 'relatedHub'])
      .optional(),
  })
  .strict()

export const HubPageSourceSchema = z
  .object({
    kind: z.enum(['generated', 'placeholder']).optional(),
    assignmentIds: z.array(NonEmptyStringSchema).default([]),
    reportIds: z.array(NonEmptyStringSchema).default([]),
    productIds: z.array(NonEmptyStringSchema).default([]),
    inputHash: NonEmptyStringSchema.optional(),
  })
  .strict()

export const HubPageSpecSchema = z
  .object({
    version: z.literal(HUB_SPEC_VERSION),
    slug: NonEmptyStringSchema,
    hubKind: HubKindSchema,
    layoutMode: HubLayoutModeSchema,
    title: NonEmptyStringSchema,
    dek: OptionalTextSchema,
    // No max(): Gemini/Vertex structured output rejects `maxItems` in the
    // generated JSON schema. Section count is bounded by the prompt instead.
    sections: z.array(HubSectionSchema).min(1),
    seo: HubSeoSchema,
    internalLinks: z.array(HubInternalLinkSchema).default([]),
    source: HubPageSourceSchema.default({
      assignmentIds: [],
      reportIds: [],
      productIds: [],
    }),
  })
  .strict()

/**
 * Loose version of {@link HubPageSpecSchema} for structured-output. Gemini /
 * Vertex `responseSchema` rejects `oneOf`, which Zod emits for the strict
 * discriminated union in `HubSectionSchema`. The agent fills this schema; the
 * caller narrows each section via the strict schema in `parseHubPageSpec`.
 */
export const HubPageSpecLooseSchema = z.object({
  version: z.literal(HUB_SPEC_VERSION),
  slug: NonEmptyStringSchema.describe(
    'URL slug for this hub page. Copy from the fixed-values block.'
  ),
  hubKind: HubKindSchema.describe(
    'Hub kind: "informational" or "transactional". Copy from the fixed-values block.'
  ),
  layoutMode: HubLayoutModeSchema.describe(
    'Layout mode. Copy from the fixed-values block.'
  ),
  title: NonEmptyStringSchema.describe('Page H1 title'),
  dek: OptionalTextSchema.describe('Subtitle shown below the title'),
  sections: z
    .array(HubSectionLooseSchema)
    .min(1)
    .describe(
      'Ordered array of page sections. Must include exactly one hero section.'
    ),
  seo: HubSeoSchema.describe(
    'SEO metadata with title, description, and canonicalPath (internal absolute path starting with /)'
  ),
  internalLinks: z
    .array(HubInternalLinkSchema)
    .optional()
    .describe('Internal links to sibling hubs or child reports'),
  source: HubPageSourceSchema.optional(),
})
// Default strip — see note on HubSectionLooseSchema.

export const HubClusterSchema = z
  .object({
    clusterSlug: NonEmptyStringSchema,
    title: NonEmptyStringSchema,
    informationalHubSlug: NonEmptyStringSchema.optional(),
    transactionalHubSlug: NonEmptyStringSchema.optional(),
    description: OptionalTextSchema,
  })
  .strict()

export const HubPlanningRowSchema = z
  .object({
    id: NonEmptyStringSchema,
    keyword: NonEmptyStringSchema,
    clusterSlug: NonEmptyStringSchema,
    taskInput: OptionalTextSchema,
    categorySlug: OptionalTextSchema,
    subcategorySlug: OptionalTextSchema,
    hubWorkflowAction: z
      .enum([
        'skip_hub_creation',
        'assign_to_hub',
        'create_report_without_hub',
        'assign_existing_report_to_hub',
      ])
      .optional(),
    informationalHubSlug: NonEmptyStringSchema.optional(),
    transactionalHubSlug: NonEmptyStringSchema.optional(),
  })
  .strict()

export const HubReportAssignmentSchema = z
  .object({
    assignmentId: NonEmptyStringSchema,
    hubSlug: NonEmptyStringSchema,
    hubKind: HubKindSchema,
    reportId: NonEmptyStringSchema,
    sourcePlanItemId: NonEmptyStringSchema.optional(),
    role: HubReportAssignmentRoleSchema.default('supporting'),
    sortOrder: NonNegativeIntegerSchema.default(0),
    assignedByEmail: z.string().email().optional(),
    assignedAt: z.string().datetime().optional(),
  })
  .strict()

export const HubGenerationInputSchema = z
  .object({
    version: z.literal(HUB_SPEC_VERSION),
    cluster: HubClusterSchema,
    hubKind: HubKindSchema,
    layoutModes: z.array(HubLayoutModeSchema).min(1).default(['structured']),
    planningRows: z.array(HubPlanningRowSchema).default([]),
    reports: z.array(HubReportRefSchema).min(3),
    products: z.array(HubProductRefSchema).default([]),
    reportCandidates: z.array(HubReportCandidateSchema).default([]),
    sourceSummary: HubSourceSummarySchema.optional(),
    assignments: z.array(HubReportAssignmentSchema).min(3),
    editorialHints: z.array(NonEmptyStringSchema).default([]),
    // Legacy field: the upstream research-brief pass was removed in the
    // hub-design-agent rewrite. Kept here as `unknown` so older
    // inputSnapshot rows that still carry a `researchBrief` payload parse
    // cleanly; the agent ignores the value.
    researchBrief: z.unknown().optional(),
    generatedAt: z.string().datetime().optional(),
  })
  .strict()

export const HubVariantBundleSchema = z
  .object({
    version: z.literal(HUB_SPEC_VERSION),
    clusterSlug: NonEmptyStringSchema,
    variants: z.array(HubPageSpecSchema).min(1),
  })
  .strict()

export type HubKind = z.infer<typeof HubKindSchema>
export type HubLayoutMode = z.infer<typeof HubLayoutModeSchema>
export type HubLayoutHint = z.infer<typeof HubLayoutHintSchema>
export type HubSectionType = z.infer<typeof HubSectionTypeSchema>
export type HubReportAssignmentRole = z.infer<
  typeof HubReportAssignmentRoleSchema
>
export type HubBuyLink = z.infer<typeof HubBuyLinkSchema>
export type HubProductRef = z.infer<typeof HubProductRefSchema>
export type HubReportRef = z.infer<typeof HubReportRefSchema>
export type HubReportSourceKind = z.infer<typeof HubReportSourceKindSchema>
export type HubReportSourceLink = z.infer<typeof HubReportSourceLinkSchema>
export type HubSourceSummary = z.infer<typeof HubSourceSummarySchema>
export type HubReportCandidateAnchor = z.infer<
  typeof HubReportCandidateAnchorSchema
>
export type HubReportCandidateQuickAnswer = z.infer<
  typeof HubReportCandidateQuickAnswerSchema
>
export type HubReportCandidateCriterion = z.infer<
  typeof HubReportCandidateCriterionSchema
>
export type HubReportCandidateHowWePicked = z.infer<
  typeof HubReportCandidateHowWePickedSchema
>
export type HubReportCandidateFaq = z.infer<typeof HubReportCandidateFaqSchema>
export type HubReportCandidateComparisonFact = z.infer<
  typeof HubReportCandidateComparisonFactSchema
>
export type HubReportCandidateTopProduct = z.infer<
  typeof HubReportCandidateTopProductSchema
>
export type HubReportCandidatePainPoint = z.infer<
  typeof HubReportCandidatePainPointSchema
>
export type HubReportCandidateEvidence = z.infer<
  typeof HubReportCandidateEvidenceSchema
>
export type HubReportCandidate = z.infer<typeof HubReportCandidateSchema>
export type HubRenderContext = z.infer<typeof HubRenderContextSchema>
export type HubByline = z.infer<typeof HubBylineSchema>
export type HubBreadcrumb = z.infer<typeof HubBreadcrumbSchema>
export type HubAnchorLinkListItem = z.infer<typeof HubAnchorLinkListItemSchema>
export type HubSection = z.infer<typeof HubSectionSchema>
export type HubHeroSection = z.infer<typeof HubHeroSectionSchema>
export type HubIntroSection = z.infer<typeof HubIntroSectionSchema>
export type HubAnchorLinkListSection = z.infer<
  typeof HubAnchorLinkListSectionSchema
>
export type HubTopPicksPick = z.infer<typeof HubTopPicksPickSchema>
export type HubTopPicksSection = z.infer<typeof HubTopPicksSectionSchema>
export type HubProductRailSection = z.infer<typeof HubProductRailSectionSchema>
export type HubReportGridItem = z.infer<typeof HubReportGridItemSchema>
export type HubReportGridSection = z.infer<typeof HubReportGridSectionSchema>
export type HubGuideStep = z.infer<typeof HubGuideStepSchema>
export type HubGuideStepsSection = z.infer<typeof HubGuideStepsSectionSchema>
export type HubGuidePathwayItem = z.infer<typeof HubGuidePathwayItemSchema>
export type HubGuidePathwaysSection = z.infer<
  typeof HubGuidePathwaysSectionSchema
>
export type HubDecisionBlockSection = z.infer<
  typeof HubDecisionBlockSectionSchema
>
export type HubConcernLinkItem = z.infer<typeof HubConcernLinkItemSchema>
export type HubConcernLinksSection = z.infer<
  typeof HubConcernLinksSectionSchema
>
export type HubComparisonColumn = z.infer<typeof HubComparisonColumnSchema>
export type HubComparisonRow = z.infer<typeof HubComparisonRowSchema>
export type HubComparisonTableSection = z.infer<
  typeof HubComparisonTableSectionSchema
>
export type HubFaqItem = z.infer<typeof HubFaqItemSchema>
export type HubFaqSection = z.infer<typeof HubFaqSectionSchema>
export type HubSourcesSummarySection = z.infer<
  typeof HubSourcesSummarySectionSchema
>
export type HubCtaBandSection = z.infer<typeof HubCtaBandSectionSchema>
export type HubPullQuoteSection = z.infer<typeof HubPullQuoteSectionSchema>
export type HubStatCalloutItem = z.infer<typeof HubStatCalloutItemSchema>
export type HubStatCalloutSection = z.infer<typeof HubStatCalloutSectionSchema>
export type HubMethodologyCardSection = z.infer<
  typeof HubMethodologyCardSectionSchema
>
export type HubReportEvidenceAnchor = z.infer<
  typeof HubReportEvidenceAnchorSchema
>
export type HubReportEvidencePick = z.infer<typeof HubReportEvidencePickSchema>
export type HubReportEvidenceDimension = z.infer<
  typeof HubReportEvidenceDimensionSchema
>
export type HubReportEvidencePainPoint = z.infer<
  typeof HubReportEvidencePainPointSchema
>
export type HubReportEvidenceCounts = z.infer<
  typeof HubReportEvidenceCountsSchema
>
export type HubReportEvidence = z.infer<typeof HubReportEvidenceSchema>
export type HubPageBlueprintSectionPlan = z.infer<
  typeof HubPageBlueprintSectionPlanSchema
>
export type HubPageBlueprint = z.infer<typeof HubPageBlueprintSchema>
export type HubCanvasResearchQuery = z.infer<
  typeof HubCanvasResearchQuerySchema
>
export type HubCanvasResearchPlan = z.infer<typeof HubCanvasResearchPlanSchema>
export type HubCanvasSearchSource = z.infer<typeof HubCanvasSearchSourceSchema>
export type HubCanvasSearchFinding = z.infer<
  typeof HubCanvasSearchFindingSchema
>
export type HubCanvasResearchBrief = z.infer<
  typeof HubCanvasResearchBriefSchema
>
export type HubEditorialReview = z.infer<typeof HubEditorialReviewSchema>
export type HubSeo = z.infer<typeof HubSeoSchema>
export type HubInternalLink = z.infer<typeof HubInternalLinkSchema>
export type HubPageSource = z.infer<typeof HubPageSourceSchema>
export type HubPageSpec = z.infer<typeof HubPageSpecSchema>
export type HubSectionLoose = z.infer<typeof HubSectionLooseSchema>
export type HubPageSpecLoose = z.infer<typeof HubPageSpecLooseSchema>
export type HubCluster = z.infer<typeof HubClusterSchema>
export type HubPlanningRow = z.infer<typeof HubPlanningRowSchema>
export type HubReportAssignment = z.infer<typeof HubReportAssignmentSchema>
export type HubGenerationInput = z.infer<typeof HubGenerationInputSchema>
export type HubVariantBundle = z.infer<typeof HubVariantBundleSchema>
